import type { Locale } from '@/lib/locale'

export type ChatbotEmbedLocale = Extract<Locale, 'en' | 'hy' | 'ru' | 'fa'>

export type ChatbotEmbedLabels = {
  title: string
  subtitle: string
}

const WIDGET_DOM_IDS = [
  'chatbot-toggle',
  'chatbot-widget',
  // The blurred backdrop behind the centered panel (C3). It must be torn down
  // with the rest: left behind, it keeps blurring the page with no chat on it.
  'chatbot-backdrop',
  'chatbot-popup-message',
  'chatbot-mobile-trigger',
  'chatbot-mobile-text-card',
  'chatbot-mobile-icon-wrap',
  'chatbot-mobile-icon',
  'chatbot-mobile-dot',
  'faq-suggestions-bubble',
  'atirame-bug-report-cta',
  'atirame-warning-help-yes',
] as const

type AtirameChatbotWindow = Window & {
  __ATIRAME_EMBED_LOADED__?: boolean
  __ATIRAME_CHATBOT_INITIALIZED__?: boolean
  __ATIRAME_CHATBOT_INITIALIZING__?: boolean
  __ATIRAME_CHATBOT_CONFIG__?: { language?: string; explicitLanguage?: string | null }
  __ATIRAME_CHATBOT_API__?: unknown
}

export function getActiveChatbotLocale(): ChatbotEmbedLocale | null {
  if (typeof window === 'undefined') return null
  const config = (window as AtirameChatbotWindow).__ATIRAME_CHATBOT_CONFIG__
  const language = config?.explicitLanguage ?? config?.language
  if (language === 'en' || language === 'hy' || language === 'ru' || language === 'fa') {
    return language
  }
  return null
}

export function isChatbotWidgetMounted(): boolean {
  if (typeof document === 'undefined') return false
  return Boolean(
    document.getElementById('chatbot-toggle') ||
      document.getElementById('chatbot-widget') ||
      (window as AtirameChatbotWindow).__ATIRAME_CHATBOT_INITIALIZED__,
  )
}

export function removeChatbotWidget(): void {
  if (typeof document === 'undefined') return

  for (const id of WIDGET_DOM_IDS) {
    document.getElementById(id)?.remove()
  }

  document
    .querySelectorAll('script[src*="chatbot-widget"], script[src*="embed-multilang"]')
    .forEach((script) => script.remove())

  const win = window as AtirameChatbotWindow
  delete win.__ATIRAME_EMBED_LOADED__
  delete win.__ATIRAME_CHATBOT_INITIALIZED__
  delete win.__ATIRAME_CHATBOT_INITIALIZING__
  delete win.__ATIRAME_CHATBOT_CONFIG__
  delete win.__ATIRAME_CHATBOT_API__
}

export async function loadChatbotLabels(locale: ChatbotEmbedLocale): Promise<ChatbotEmbedLabels> {
  const response = await fetch(`/locales/${locale}.json`, { cache: 'no-store' })
  if (!response.ok) {
    throw new Error(`Failed to load chatbot labels for locale: ${locale}`)
  }

  const data = (await response.json()) as {
    chatbot?: { title?: string; subtitle?: string }
  }

  return {
    title: data.chatbot?.title ?? 'Atiram Smart Assistant',
    subtitle: data.chatbot?.subtitle ?? 'How can I help you?',
  }
}

export function mountChatbotEmbed(
  locale: ChatbotEmbedLocale,
  labels: ChatbotEmbedLabels,
): void {
  if (typeof document === 'undefined') return

  const script = document.createElement('script')
  script.src = '/dist2/embed-multilang.js'
  script.setAttribute('data-position', 'bottom-right')
  script.setAttribute('data-language', locale)
  script.setAttribute('data-custom-title', labels.title)
  script.setAttribute('data-custom-subtitle', labels.subtitle)
  script.async = true
  script.defer = true
  document.body.appendChild(script)

  ;(window as AtirameChatbotWindow).__ATIRAME_EMBED_LOADED__ = true
}

export async function ensureChatbotEmbed(
  locale: ChatbotEmbedLocale,
  labels?: ChatbotEmbedLabels,
): Promise<void> {
  const activeLocale = getActiveChatbotLocale()
  if (isChatbotWidgetMounted() && activeLocale === locale) {
    return
  }

  removeChatbotWidget()

  const resolvedLabels = labels ?? (await loadChatbotLabels(locale))
  mountChatbotEmbed(locale, resolvedLabels)
}
